import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faCompactDisc,
  faMusic,
  faArrowLeft,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./RecommendationsDrawer.scss";

const API_BASE =
  process.env.REACT_APP_DISCOVERY_API_URL ||
  "https://api.spinninbeans.com";

function formatDuration(ms) {
  if (!ms) return "";
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

const RecommendationsDrawer = ({
  seedTrack,
  isOpen,
  onClose,
  onPlayTrack,
  onPause,
  currentTrack,
  isPlaying,
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seedStack, setSeedStack] = useState([]);
  const [activeSeed, setActiveSeed] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const touchStartY = useRef(null);
  const drawerRef = useRef(null);

  // Set active seed when seedTrack changes
  useEffect(() => {
    if (seedTrack && isOpen) {
      setActiveSeed(seedTrack);
      setSeedStack([]);
    }
  }, [seedTrack, isOpen]);

  // Fetch recommendations when active seed changes
  useEffect(() => {
    if (!activeSeed) return;

    const fetchRecs = async () => {
      setLoading(true);
      try {
        const seedId = `${activeSeed.platform}_${activeSeed.platform_track_id}`;
        const res = await fetch(
          `${API_BASE}/api/search?seed_track_id=${seedId}&limit=20`
        );
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setRecommendations(data.tracks || []);
      } catch (err) {
        console.error("Recommendations fetch failed:", err);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecs();
  }, [activeSeed]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  });

  // Browser back button
  useEffect(() => {
    if (!isOpen) return;
    window.history.pushState({ recommendations: true }, "");
    const handlePop = () => onClose();
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [isOpen, onClose]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  const drillDown = (track) => {
    setSeedStack((prev) => [...prev, activeSeed]);
    setActiveSeed(track);
  };

  const goBack = () => {
    const prev = seedStack[seedStack.length - 1];
    setSeedStack((s) => s.slice(0, -1));
    setActiveSeed(prev);
  };

  // Mobile touch-to-dismiss
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (deltaY > 100) handleClose();
    touchStartY.current = null;
  };

  if (!isOpen && !isClosing) return null;
  if (!activeSeed) return null;

  return createPortal(
    <div className="rec-overlay" onClick={handleClose}>
      <div
        className={`rec-drawer ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
        ref={drawerRef}
      >
        {/* Mobile drag handle */}
        <div
          className="rec-drag-handle"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="rec-drag-bar" />
        </div>

        {/* Header */}
        <div className="rec-header">
          {seedStack.length > 0 && (
            <button className="rec-back-btn" onClick={goBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <div className="rec-seed-info">
            {activeSeed.primary_image_url ? (
              <img
                src={activeSeed.primary_image_url}
                alt=""
                className="rec-seed-art"
              />
            ) : (
              <div className="rec-seed-art-placeholder">
                <FontAwesomeIcon icon={faMusic} />
              </div>
            )}
            <div className="rec-seed-text">
              <div className="rec-seed-title">
                {activeSeed.primary_title || "Unknown Title"}
              </div>
              <div className="rec-seed-artist">
                {activeSeed.artist_norm || "Unknown Artist"}
              </div>
            </div>
          </div>
          <button className="rec-close-btn" onClick={handleClose}>
            &times;
          </button>
        </div>

        {/* Seed track meta tags */}
        <div className="rec-seed-meta">
          {activeSeed.bpm && (
            <span className="meta-tag">
              {Math.round(activeSeed.bpm)} BPM
            </span>
          )}
          {activeSeed.camelot_key && (
            <span className="meta-tag key">{activeSeed.camelot_key}</span>
          )}
          {activeSeed.primary_genre && (
            <span className="meta-tag genre">{activeSeed.primary_genre}</span>
          )}
          {activeSeed.duration_ms && (
            <span className="meta-tag">
              {formatDuration(activeSeed.duration_ms)}
            </span>
          )}
        </div>

        {/* Section label */}
        <div className="rec-section-label">
          <span>Similar Tracks</span>
          {!loading && recommendations.length > 0 && (
            <span className="rec-count">{recommendations.length}</span>
          )}
        </div>

        {/* Recommendation list */}
        <div className="rec-list">
          {loading ? (
            <div className="rec-loading">
              <FontAwesomeIcon
                icon={faCompactDisc}
                spin
                className="rec-loading-icon"
              />
              <p>Finding similar tracks...</p>
            </div>
          ) : recommendations.length === 0 ? (
            <div className="rec-empty">
              <FontAwesomeIcon
                icon={faMusic}
                className="rec-loading-icon"
              />
              <p>No similar tracks found.</p>
            </div>
          ) : (
            recommendations.map((track) => {
              const isTrackPlaying =
                isPlaying && currentTrack?.id === track.id;

              return (
                <div
                  key={track.id}
                  className="rec-item"
                  onClick={() => drillDown(track)}
                >
                  <div className="rec-item-art-wrap">
                    {track.primary_image_url ? (
                      <img
                        src={track.primary_image_url}
                        alt=""
                        className="rec-item-art"
                      />
                    ) : (
                      <div className="rec-item-art-placeholder">
                        <FontAwesomeIcon icon={faCompactDisc} />
                      </div>
                    )}
                    <button
                      className={`rec-item-play ${isTrackPlaying ? "visible" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isTrackPlaying) {
                          onPause();
                        } else {
                          onPlayTrack(track);
                        }
                      }}
                    >
                      <FontAwesomeIcon
                        icon={isTrackPlaying ? faPause : faPlay}
                      />
                    </button>
                  </div>

                  <div className="rec-item-info">
                    <div className="rec-item-title">
                      {track.primary_title || "Unknown Title"}
                    </div>
                    <div className="rec-item-artist">
                      {track.artist_norm || "Unknown Artist"}
                    </div>
                    <div className="rec-item-meta">
                      {track.bpm && (
                        <span className="meta-tag">
                          {Math.round(track.bpm)} BPM
                        </span>
                      )}
                      {track.camelot_key && (
                        <span className="meta-tag key">
                          {track.camelot_key}
                        </span>
                      )}
                      {track.similarity_score > 0 && (
                        <span className="meta-tag similarity">
                          {Math.round(track.similarity_score * 100)}% match
                        </span>
                      )}
                    </div>
                  </div>

                  {track.external_url && (
                    <a
                      href={track.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rec-item-external"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecommendationsDrawer;
